import { DoctorBlockedTab } from "../../components/DoctorBlockedTab";
export { DoctorBlockedTab };
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { useClinicLinks } from "./ClinicAdminPages";
import { ActionPromptModal } from "../../components/ActionPromptModal";
import api from "../../lib/api";
import { toast } from "sonner";
import { useCan } from "../../lib/permissions";
import {
  Search, Plus, Pencil, Trash2, RefreshCcw, Eye, ArrowLeft, Save, X, Upload,
  Calendar, ClipboardList, Stethoscope, AlertTriangle, Mail, Phone, Award,
  Clock, Coffee, ShieldCheck, Activity, RefreshCw, CircleHelp,
} from "lucide-react";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ============================================================
// LIST PAGE
// ============================================================
export function ClinicDoctorsListPage() {
  const links = useClinicLinks();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [spec, setSpec] = useState("");
  const [onlineFilter, setOnlineFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkReasonModal, setBulkReasonModal] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const canCreate = useCan("doctors.create");
  const canBulk = useCan("doctors.bulk_actions");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (statusFilter) params.append("status_filter", statusFilter);
      if (spec) params.append("specialization", spec);
      if (onlineFilter) params.append("online_booking", onlineFilter);
      const r = await api.get(`/clinic/doctors-x?${params.toString()}`);
      setDoctors(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [q, statusFilter, spec, onlineFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const runBulk = async (action) => {
    if (!selected.size) return toast.error("No doctors selected");
    setBulkReasonModal({ action, ids: [...selected] });
  };

  const submitBulk = async (reason) => {
    if (!bulkReasonModal) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      toast.error("Reason is required");
      return;
    }
    setBulkSubmitting(true);
    try {
      const r = await api.post("/clinic/doctors-x/bulk", {
        doctor_ids: bulkReasonModal.ids,
        action: bulkReasonModal.action,
        reason: trimmedReason,
      });
      toast.success(`${r.data.ok.length} ok, ${r.data.failed.length} failed`);
      setSelected(new Set());
      setBulkReasonModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    }
    setBulkSubmitting(false);
  };

  const exportCsv = () => {
    if (!doctors.length) return toast.error("No data");
    const rows = doctors.map((d) => ({
      doctor_id: d.doctor_id, name: d.name, email: d.email, phone: d.phone || "",
      specialization: d.specialization || "", qualification: d.qualification || "",
      registration_number: d.registration_number || "", fee: d.consultation_fee || 0,
      status: d.status, appts_today: d.appointments_today, upcoming: d.upcoming_appointments,
    }));
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `medigenie-doctors-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <DashboardLayout links={links} title="Doctors">
      <div className="mg-card mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="mg-input pl-9 w-full" placeholder="Search name/email/phone/spec…"
              value={q} onChange={(e) => setQ(e.target.value)} data-testid="doctors-search" />
          </div>
          <input className="mg-input text-sm" placeholder="Specialization" value={spec}
            onChange={(e) => setSpec(e.target.value)} data-testid="doctors-spec-filter" />
          <select className="mg-input text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="doctors-status-filter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
          </select>
          <select className="mg-input text-sm" value={onlineFilter} onChange={(e) => setOnlineFilter(e.target.value)} data-testid="doctors-online-filter">
            <option value="">Any online state</option>
            <option value="true">Online booking on</option>
            <option value="false">Online booking off</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={exportCsv} className="mg-btn-secondary text-sm" data-testid="doctors-export">CSV</button>
            <button onClick={load} className="mg-btn-secondary text-sm" data-testid="doctors-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            {canCreate && (
              <Link to="/clinic-admin/doctors/new" className="mg-btn-primary text-sm" data-testid="add-doctor-btn">
                <Plus className="w-4 h-4" /> New doctor
              </Link>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && canBulk && (
        <div className="mg-card mb-4 bg-mg-medical/5 border-mg-medical/20" data-testid="doctor-bulk-bar">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-mg-navy">{selected.size} selected</div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => runBulk("activate")} className="mg-btn-secondary text-xs" data-testid="bulk-activate">Activate</button>
              <button onClick={() => runBulk("deactivate")} className="mg-btn-secondary text-xs" data-testid="bulk-deactivate">Deactivate</button>
              <button onClick={() => runBulk("soft_delete")} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="bulk-soft-delete">Delete</button>
              <button onClick={() => runBulk("restore")} className="mg-btn-secondary text-xs" data-testid="bulk-restore">Restore</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <ActionPromptModal
        open={Boolean(bulkReasonModal)}
        title={bulkReasonModal ? `Reason for bulk ${bulkReasonModal.action}` : "Bulk action reason"}
        label="Reason"
        placeholder="Bulk action"
        initialValue="Bulk action"
        confirmLabel="Apply"
        busy={bulkSubmitting}
        onCancel={() => setBulkReasonModal(null)}
        onConfirm={submitBulk}
      />

      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="doctors-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr>
              {canBulk && <th className="w-8 px-3 py-3"><input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(doctors.map((d) => d.doctor_id)) : new Set())} data-testid="select-all-doctors" /></th>}
              <th className="px-2 py-3">Doctor</th>
              <th>Specialization</th>
              <th>Phone</th>
              <th className="text-right">Fee</th>
              <th className="text-center">Today</th>
              <th className="text-center">Upcoming</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center text-slate-400 py-8">Loading…</td></tr>}
            {!loading && doctors.length === 0 && <tr><td colSpan="9" className="text-center text-slate-400 py-8">No doctors found.</td></tr>}
            {!loading && doctors.map((d) => (
              <tr key={d.doctor_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`doctor-row-${d.doctor_id}`}>
                {canBulk && <td className="px-3 py-3"><input type="checkbox" checked={selected.has(d.doctor_id)} onChange={() => toggleSelect(d.doctor_id)} data-testid={`select-${d.doctor_id}`} /></td>}
                <td className="px-2 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={d.profile_image_path} name={d.name} />
                    <div>
                      <Link to={`/clinic-admin/doctors/${d.doctor_id}`} className="font-bold text-mg-navy hover:text-mg-medical" data-testid={`doctor-name-${d.doctor_id}`}>{d.name}</Link>
                      <div className="text-[10px] text-slate-500">{d.email}</div>
                    </div>
                  </div>
                </td>
                <td className="text-xs">{d.specialization || "—"}<div className="text-[10px] text-slate-500">{d.qualification || ""}</div></td>
                <td className="text-xs">{d.phone || "—"}</td>
                <td className="text-right text-xs font-semibold">₹{(d.consultation_fee || 0).toLocaleString()}</td>
                <td className="text-center text-xs">{d.appointments_today}</td>
                <td className="text-center text-xs">{d.upcoming_appointments}</td>
                <td><span className={`mg-status mg-status-${d.status}`}>{d.status}</span></td>
                <td className="text-right">
                  <Link to={`/clinic-admin/doctors/${d.doctor_id}`} className="p-1.5 hover:bg-slate-100 rounded inline-block" data-testid={`view-${d.doctor_id}`}>
                    <Eye className="w-4 h-4 text-mg-medical" />
                  </Link>
                  <Link to={`/clinic-admin/doctors/${d.doctor_id}/edit`} className="p-1.5 hover:bg-slate-100 rounded inline-block ml-1" data-testid={`edit-${d.doctor_id}`}>
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// AVATAR helper
// ============================================================
function Avatar({ src, name, size = "w-10 h-10" }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return src ? (
    <img src={`${apiBase}${src}`} alt={name} className={`${size} rounded-full object-cover`} />
  ) : (
    <div className={`${size} rounded-full bg-mg-medical/10 text-mg-medical font-bold flex items-center justify-center text-xs`}>{initials}</div>
  );
}

// ============================================================
// DETAIL PAGE
// ============================================================
const DETAIL_TABS = [
  { key: "profile", label: "Profile", icon: Stethoscope },
  { key: "services", label: "Services", icon: ClipboardList },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "leaves", label: "Leaves", icon: Coffee },
  { key: "blocked", label: "Blocked slots", icon: Clock },
  { key: "stats", label: "Stats", icon: Activity },
  { key: "activity", label: "Activity", icon: ShieldCheck },
];

import DoctorBranchAssignments from "./DoctorBranchAssignments";

export function ClinicDoctorDetailPage() {
  const links = useClinicLinks();
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const requestedTab = searchParams.get("tab");
  const [tab, setTab] = useState(DETAIL_TABS.some((t) => t.key === requestedTab) ? requestedTab : "profile");
  const canEdit = useCan("doctors.edit");
  const canDelete = useCan("doctors.delete");
  const canViewRevenue = useCan("doctors.view_revenue");
  const canUploadImg = useCan("doctors.upload_image");

  const load = useCallback(() => {
    api.get(`/clinic/doctors-x/${id}/detail`).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const onDelete = async () => {
    if (!window.confirm("Soft-delete this doctor?")) return;
    try { await api.delete(`/clinic/doctors-x/${id}`); toast.success("Deleted"); nav("/clinic-admin/doctors"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onRestore = async () => {
    try { await api.post(`/clinic/doctors-x/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Image too large (max 3MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/clinic/doctors-x/${id}/avatar`, { data_url: reader.result });
        toast.success("Avatar updated"); load();
      } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  if (!data) return <DashboardLayout links={links} title="Doctor"><div className="mg-card text-center text-slate-400 py-10">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout links={links} title={data.name}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Link to="/clinic-admin/doctors" className="text-sm text-mg-medical font-semibold inline-flex items-center gap-1" data-testid="back-to-doctors">
          <ArrowLeft className="w-4 h-4" /> All doctors
        </Link>
        <div className="flex items-center gap-2">
          {canEdit && <Link to={`/clinic-admin/doctors/${id}/edit`} className="mg-btn-secondary text-sm" data-testid="doctor-edit"><Pencil className="w-4 h-4" /> Edit</Link>}
          {canDelete && data.status !== "deleted" && (
            <button onClick={onDelete} className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50" data-testid="doctor-delete">
              <Trash2 className="w-4 h-4 inline" /> Delete
            </button>
          )}
          {data.status === "deleted" && (
            <button onClick={onRestore} className="mg-btn-primary text-sm" data-testid="doctor-restore">
              <RefreshCcw className="w-4 h-4" /> Restore
            </button>
          )}
        </div>
      </div>

      <div className="mg-card mb-4 flex items-start gap-4 flex-wrap">
        <div className="relative">
          <Avatar src={data.profile_image_path} name={data.name} size="w-20 h-20" />
          {canUploadImg && (
            <label className="absolute -bottom-1 -right-1 cursor-pointer bg-white rounded-full p-1 shadow border border-slate-200 hover:bg-slate-50" data-testid="avatar-upload-label">
              <Upload className="w-3.5 h-3.5 text-mg-medical" />
              <input type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
            </label>
          )}
        </div>
        <div className="flex-1 min-w-[260px]">
          <h2 className="text-xl font-extrabold text-mg-navy" data-testid="doctor-detail-name">{data.name}</h2>
          <div className="text-xs text-slate-500 mt-1">{data.specialization} {data.qualification ? `· ${data.qualification}` : ""}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 text-xs">
            <Stat label="Today" value={data.appointment_stats?.upcoming || 0} />
            <Stat label="Completed" value={data.appointment_stats?.completed || 0} />
            <Stat label="No-shows" value={data.appointment_stats?.no_show || 0} />
            <Stat label="Active enc." value={data.encounters_active} />
            {canViewRevenue && <Stat label="Revenue" value={`₹${(data.revenue_total || 0).toLocaleString()}`} />}
          </div>
        </div>
      </div>

      <div className="mg-card overflow-x-auto p-0 mb-4">
        <div className="flex border-b border-slate-100">
          {DETAIL_TABS.map((t) => {
            const I = t.icon;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setSearchParams(t.key === "profile" ? {} : { tab: t.key }); }}
                className={`px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === t.key ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
                data-testid={`doctor-tab-${t.key}`}>
                <I className="w-3.5 h-3.5" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "profile" && <><DoctorProfileTab d={data} />{canEdit && data.status === "active" && <DoctorBranchAssignments doctorId={id} />}</>}
      {tab === "services" && <DoctorServicesTab doctorId={id} />}
      {tab === "schedule" && <DoctorScheduleTab doctorId={id} schedule={data.schedule} onChange={load} />}
      {tab === "leaves" && <DoctorLeavesTab doctorId={id} leaves={data.leaves} onChange={load} />}
      {tab === "blocked" && <DoctorBlockedTab doctorId={id} blocked={data.blocked_slots} onChange={load} />}
      {tab === "stats" && <DoctorStatsTab d={data} />}
      {tab === "activity" && <DoctorActivityTab activity={data.activity} />}
    </DashboardLayout>
  );
}

function DoctorServicesTab({ doctorId }) {
  const canAssign = useCan("doctor_services.assign");
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, assigned] = await Promise.all([
        api.get("/clinic/services-x?status_filter=active"),
        api.get(`/clinic/doctor-service/${doctorId}/services`),
      ]);
      setServices(all.data || []);
      setSelected(new Set((assigned.data || []).map((a) => a.service_id)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load services"); }
    setLoading(false);
  }, [doctorId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clinic/doctor-service/${doctorId}/services`, { service_ids: [...selected] });
      toast.success("Doctor services updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update services"); }
    setSaving(false);
  };
  return <div className="mg-card" data-testid="doctor-services-tab">
    <div className="flex items-center justify-between mb-3"><div><h3 className="font-bold text-mg-navy">Services offered by this doctor</h3><p className="text-xs text-slate-500 mt-1">Select one or more active clinic services.</p></div>{canAssign && <button onClick={save} disabled={saving || loading} className="mg-btn-primary text-sm" data-testid="save-doctor-services">{saving ? "Saving…" : "Save services"}</button>}</div>
    {loading ? <div className="text-sm text-slate-400 py-6">Loading…</div> : !services.length ? <div className="text-sm text-slate-400 py-6">Create an active service first.</div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{services.map((s) => <label key={s.service_id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-3 hover:bg-slate-50"><input type="checkbox" checked={selected.has(s.service_id)} disabled={!canAssign || saving} onChange={() => setSelected((prev) => { const next = new Set(prev); next.has(s.service_id) ? next.delete(s.service_id) : next.add(s.service_id); return next; })} data-testid={`doctor-service-${s.service_id}`} /><span><span className="block text-sm font-semibold text-mg-navy">{s.name}</span><span className="block text-xs text-slate-500">{s.duration_minutes || 30} min · ₹{s.price || 0}</span></span></label>)}</div>}
  </div>;
}

function Stat({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div><div className="text-base font-extrabold text-mg-navy">{value}</div></div>;
}

function Row({ icon, label, value }) {
  return <div className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
    <div className="text-slate-400 mt-0.5">{icon}</div>
    <div className="flex-1"><div className="text-[10px] uppercase font-bold text-slate-500">{label}</div><div className="text-sm text-mg-navy">{value || "—"}</div></div>
  </div>;
}

function DoctorProfileTab({ d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">Contact</h3>
        <Row icon={<Mail className="w-4 h-4" />} label="Email" value={d.email} />
        <Row icon={<Phone className="w-4 h-4" />} label="Phone" value={d.phone} />
        <Row label="Gender" value={d.gender} />
        <Row label="DOB" value={d.date_of_birth} />
      </div>
      <div className="mg-card">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">Professional</h3>
        <Row icon={<Award className="w-4 h-4" />} label="Specialization" value={d.specialization} />
        <Row label="Sub-specialization" value={d.sub_specialization} />
        <Row label="Qualification" value={d.qualification} />
        <Row label="Registration #" value={d.registration_number} />
        <Row label="Council" value={d.registration_council} />
        <Row label="Department" value={d.department} />
        <Row label="Designation" value={d.designation} />
        <Row label="Experience" value={d.years_of_experience ? `${d.years_of_experience} years` : null} />
      </div>
      <div className="mg-card md:col-span-2">
        <h3 className="font-bold text-mg-navy mb-2 text-sm">Settings</h3>
        <Row label="Consultation fee" value={`₹${(d.consultation_fee || 0).toLocaleString()}`} />
        <Row label="Follow-up fee" value={d.followup_fee != null ? `₹${d.followup_fee.toLocaleString()}` : null} />
        <Row label="Default duration" value={`${d.default_consultation_duration || 15} min`} />
        <Row label="Online booking" value={d.online_booking_enabled ? "Enabled" : "Disabled"} />
        <Row label="Calendar sync" value={d.calendar_sync_enabled ? "Enabled" : "Disabled"} />
        <Row label="Languages" value={(d.languages || []).join(", ")} />
        <Row label="Bio" value={d.bio} />
      </div>
    </div>
  );
}

function ScheduleHelp({ text }) {
  return <span title={text} aria-label={text} className="inline-flex cursor-help text-slate-400 hover:text-mg-medical"><CircleHelp className="w-4 h-4" /></span>;
}

export function DoctorScheduleTab({ doctorId, schedule, onChange, selfService = false, timezone }) {
  const permissionCanEdit = useCan("doctors.manage_schedule");
  const canEdit = selfService || permissionCanEdit;
  const [clinicTimezone, setClinicTimezone] = useState(timezone || "Asia/Kolkata");
  useEffect(() => {
    if (timezone) { setClinicTimezone(timezone); return; }
    api.get("/clinic/profile").then((r) => setClinicTimezone(r.data.timezone || "Asia/Kolkata")).catch(() => {});
  }, [timezone]);
  const buildRows = useCallback(() => {
    const map = {};
    (schedule || []).forEach((s) => { map[s.day_of_week] = { ...s, periods: s.periods || (s.is_working_day ? [{ start_time: s.start_time || "", end_time: s.end_time || "" }] : []) }; });
    return [0, 1, 2, 3, 4, 5, 6].map((d) => map[d] || {
      day_of_week: d, is_working_day: false, start_time: "", end_time: "",
      break_start_time: "", break_end_time: "", default_slot_duration: 15,
      max_appointments_per_day: null, max_appointments_per_slot: 1,
      online_booking_enabled: true,
      periods: [],
    });
  }, [schedule]);
  const [rows, setRows] = useState(buildRows());
  useEffect(() => { setRows(buildRows()); }, [buildRows]);
  const [busy, setBusy] = useState(false);
  const update = (i, field, val) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [field]: val } : x));
  const toggleWorkingDay = (i, enabled) => setRows((current) => current.map((row, index) => index !== i ? row : ({
    ...row,
    is_working_day: enabled,
    periods: enabled && !(row.periods || []).length ? [{ start_time: "09:00", end_time: "17:00" }] : row.periods,
  })));
  const updatePeriod = (rowIndex, periodIndex, field, value) => setRows((current) => current.map((row, index) => index !== rowIndex ? row : ({ ...row, periods: (row.periods || []).map((period, pIndex) => pIndex === periodIndex ? { ...period, [field]: value } : period) })));
  const addPeriod = (rowIndex) => setRows((current) => current.map((row, index) => index !== rowIndex ? row : ({ ...row, is_working_day: true, periods: [...(row.periods || []), { start_time: "", end_time: "" }] })));
  const removePeriod = (rowIndex, periodIndex) => setRows((current) => current.map((row, index) => index !== rowIndex ? row : ({ ...row, periods: (row.periods || []).filter((_, pIndex) => pIndex !== periodIndex) })));
  const copyFrom = (i) => {
    const src = rows[i];
    setRows((r) => r.map((x, idx) => idx !== i ? { ...src, day_of_week: idx } : x));
    toast.success("Copied to all days");
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = rows.map((r) => {
        const periods = r.is_working_day ? (r.periods || []) : [];
        return { ...r, periods, start_time: periods[0]?.start_time || null, end_time: periods[periods.length - 1]?.end_time || null,
          break_start_time: null, break_end_time: null };
      });
      await api.put(selfService ? "/clinic/doctors-x/me/schedule" : `/clinic/doctors-x/${doctorId}/schedule`, payload);
      toast.success("Schedule saved"); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  return (
    <div className="mg-card" data-testid="schedule-grid">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6 p-5 rounded-2xl bg-blue-50 border border-blue-100" data-testid="schedule-timezone-preview">
        <div><div className="flex items-center gap-2 font-bold text-mg-navy"><Clock className="w-5 h-5 text-mg-medical" />Weekly availability</div><p className="text-sm text-slate-600 mt-1">Add each block when the doctor is available. Any gap between blocks is automatically treated as a break. Times use the <strong>{clinicTimezone}</strong> timezone.</p></div>
        <div className="lg:text-right"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Current schedule</p><p className="text-sm font-semibold text-mg-navy mt-1">{rows.filter((r) => r.is_working_day).map((r) => `${DAY_NAMES[r.day_of_week]} ${(r.periods || []).map((p) => `${p.start_time || "—"}–${p.end_time || "—"}`).join(", ")}`).join(" · ") || "No working days selected"}</p></div>
      </div>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <section key={i} className={`rounded-2xl border transition-colors ${r.is_working_day ? "border-blue-200 bg-white shadow-sm" : "border-slate-200 bg-slate-50"}`} data-testid={`sched-row-${i}`}>
            <div className="flex items-center justify-between gap-4 p-4 sm:px-5">
              <div className="flex items-center gap-3"><input type="checkbox" className="w-4 h-4 accent-blue-600" checked={r.is_working_day} onChange={(e) => toggleWorkingDay(i, e.target.checked)} disabled={!canEdit} data-testid={`sched-working-${i}`} /><div><h3 className="font-bold text-mg-navy">{DAY_NAMES[i]}</h3><p className="text-xs text-slate-500">{r.is_working_day ? (r.periods || []).map((p) => `${p.start_time}–${p.end_time}`).join(", ") : "Not available for appointments"}</p></div></div>
              {canEdit && r.is_working_day && <button type="button" onClick={() => copyFrom(i)} className="text-xs text-mg-medical font-bold hover:underline" data-testid={`copy-${i}`}>Use these settings for every day</button>}
            </div>
            {r.is_working_day && <div className="border-t border-slate-100 p-4 sm:p-5">
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                <div className="xl:col-span-2"><div className="flex items-center gap-2 text-xs font-bold text-slate-700 mb-2">Available working blocks <ScheduleHelp text="Enter every block when appointments may be booked. Gaps between blocks are breaks, so you can create as many breaks as needed." /></div><div className="space-y-2">{(r.periods || []).map((period, periodIndex) => <div key={periodIndex}>{periodIndex > 0 && <div className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5 mb-2 inline-block">Break: {(r.periods || [])[periodIndex - 1]?.end_time || "—"} to {period.start_time || "—"}</div>}<div className="flex items-center gap-2"><span className="w-16 text-xs font-semibold text-slate-500">Block {periodIndex + 1}</span><input aria-label={`${DAY_NAMES[i]} period ${periodIndex + 1} start`} type="time" className="mg-input max-w-40" value={period.start_time} onChange={(e) => updatePeriod(i, periodIndex, "start_time", e.target.value)} disabled={!canEdit}/><span className="text-sm text-slate-400">to</span><input aria-label={`${DAY_NAMES[i]} period ${periodIndex + 1} end`} type="time" className="mg-input max-w-40" value={period.end_time} onChange={(e) => updatePeriod(i, periodIndex, "end_time", e.target.value)} disabled={!canEdit}/>{canEdit && (r.periods || []).length > 1 && <button type="button" aria-label={`Remove ${DAY_NAMES[i]} period ${periodIndex + 1}`} onClick={() => removePeriod(i, periodIndex)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>}</div></div>)}</div>{canEdit && <button type="button" className="mt-3 text-xs text-mg-medical font-bold" onClick={() => addPeriod(i)} data-testid={`add-period-${i}`}>+ Add another working block</button>}</div>
                <label className="text-xs font-bold text-slate-700"><span className="flex items-center gap-2 mb-2">Appointment length <ScheduleHelp text="The standard time reserved for one appointment on this day." /></span><div className="relative"><input type="number" min="5" className="mg-input pr-16" value={r.default_slot_duration || ""} onChange={(e) => update(i, "default_slot_duration", parseInt(e.target.value) || null)} disabled={!canEdit} data-testid={`sched-duration-${i}`} /><span className="absolute right-3 top-3 text-xs text-slate-400">minutes</span></div></label>
                <label className="text-xs font-bold text-slate-700"><span className="flex items-center gap-2 mb-2">Gap after each appointment <ScheduleHelp text="Extra preparation or rest time added after every appointment. Enter 0 if no gap is needed." /></span><div className="relative"><input type="number" min="0" className="mg-input pr-16" value={r.buffer_minutes ?? ""} onChange={(e) => update(i, "buffer_minutes", parseInt(e.target.value) || 0)} disabled={!canEdit}/><span className="absolute right-3 top-3 text-xs text-slate-400">minutes</span></div></label>
                <label className="text-xs font-bold text-slate-700"><span className="flex items-center gap-2 mb-2">Travel time between branches <ScheduleHelp text="Time protected when the doctor's next appointment is at another branch. Enter 0 if the doctor works at one location." /></span><div className="relative"><input type="number" min="0" className="mg-input pr-16" value={r.travel_buffer_minutes ?? ""} onChange={(e) => update(i, "travel_buffer_minutes", parseInt(e.target.value) || 0)} disabled={!canEdit}/><span className="absolute right-3 top-3 text-xs text-slate-400">minutes</span></div></label>
                <label className="text-xs font-bold text-slate-700"><span className="flex items-center gap-2 mb-2">Daily appointment limit <ScheduleHelp text="The maximum number of appointments allowed on this day. Leave blank for no daily limit." /></span><input type="number" min="0" placeholder="No limit" className="mg-input" value={r.max_appointments_per_day || ""} onChange={(e) => update(i, "max_appointments_per_day", parseInt(e.target.value) || null)} disabled={!canEdit}/></label>
              </div>
              <label className="mt-5 pt-4 border-t flex items-center gap-3 text-sm font-semibold text-mg-navy"><input type="checkbox" className="w-4 h-4 accent-blue-600" checked={r.online_booking_enabled} onChange={(e) => update(i, "online_booking_enabled", e.target.checked)} disabled={!canEdit}/><span>Allow patients to book these times online</span><ScheduleHelp text="When enabled, available times can appear on the public booking page. Staff can still book appointments internally when this is off." /></label>
            </div>}
          </section>
        ))}
      </div>
      {canEdit && (
        <div className="sticky bottom-0 flex justify-end mt-6 pt-4 border-t bg-white/95 backdrop-blur">
          <button onClick={save} disabled={busy} className="mg-btn-primary text-sm" data-testid="save-schedule">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save schedule"}
          </button>
        </div>
      )}
    </div>
  );
}

export function DoctorLeavesTab({ doctorId, leaves, onChange, selfService = false }) {
  const permissionCanCreate = useCan("doctor_leaves.create");
  const permissionCanDelete = useCan("doctor_leaves.delete");
  const canCreate = selfService || permissionCanCreate;
  const canDelete = selfService || permissionCanDelete;
  const [form, setForm] = useState({ leave_date: "", is_full_day: true, start_time: "", end_time: "", reason: "", leave_type: "personal" });
  const add = async (e) => {
    e.preventDefault();
    try {
      const p = { ...form, start_time: form.start_time || null, end_time: form.end_time || null };
      await api.post(selfService ? "/clinic/doctors-x/me/leaves" : `/clinic/doctors-x/${doctorId}/leaves`, p);
      toast.success("Leave added"); setForm({ leave_date: "", is_full_day: true, start_time: "", end_time: "", reason: "", leave_type: "personal" });
      onChange();
    } catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
  };
  const remove = async (lid) => {
    if (!window.confirm("Cancel this leave?")) return;
    try { await api.delete(selfService ? `/clinic/doctors-x/me/leaves/${lid}` : `/clinic/doctors-x/${doctorId}/leaves/${lid}`); toast.success("Cancelled"); onChange(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      {canCreate && (
        <form onSubmit={add} className="mg-card" data-testid="leave-form">
          <h3 className="font-bold text-mg-navy mb-2 text-sm">Add leave</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input type="date" className="mg-input text-sm" required value={form.leave_date} onChange={(e) => setForm({ ...form, leave_date: e.target.value })} data-testid="leave-date" />
            <select className="mg-input text-sm" value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="leave-type">
              <option value="personal">Personal</option>
              <option value="emergency">Emergency</option>
              <option value="conference">Conference</option>
              <option value="full_day">Full-day</option>
              <option value="partial_day">Partial-day</option>
            </select>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.is_full_day} onChange={(e) => setForm({ ...form, is_full_day: e.target.checked })} data-testid="leave-full-day" /> Full day
            </label>
            {!form.is_full_day && (
              <>
                <input type="time" className="mg-input text-sm" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="leave-start" />
                <input type="time" className="mg-input text-sm" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="leave-end" />
              </>
            )}
            <input className="mg-input text-sm md:col-span-3" placeholder="Reason" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" />
          </div>
          <div className="flex justify-end mt-3">
            <button type="submit" className="mg-btn-primary text-sm" data-testid="add-leave-btn"><Plus className="w-4 h-4" /> Add leave</button>
          </div>
        </form>
      )}
      <div className="mg-card overflow-x-auto p-0" data-testid="leaves-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="px-3 py-3">Date</th><th>Type</th><th>Time</th><th>Reason</th><th>Status</th>{canDelete && <th></th>}</tr>
          </thead>
          <tbody>
            {(leaves || []).length === 0 && <tr><td colSpan="6" className="text-center text-slate-400 py-8">No leaves.</td></tr>}
            {(leaves || []).map((l) => (
              <tr key={l.leave_id} className="border-b border-slate-50" data-testid={`leave-${l.leave_id}`}>
                <td className="px-3 py-2 font-semibold">{l.leave_date}</td>
                <td className="text-xs">{l.leave_type}</td>
                <td className="text-xs">{l.is_full_day ? "Full day" : `${l.start_time}–${l.end_time}`}</td>
                <td className="text-xs">{l.reason}</td>
                <td><span className={`mg-status mg-status-${l.status === "active" ? "active" : "cancelled"}`}>{l.status}</span></td>
                {canDelete && <td className="text-right"><button onClick={() => remove(l.leave_id)} className="text-red-500" data-testid={`del-leave-${l.leave_id}`} aria-label="Delete leave"><X className="w-4 h-4 inline" aria-hidden="true" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DoctorStatsTab({ d }) {
  const s = d.appointment_stats || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card label="Total appointments" value={s.total || 0} />
      <Card label="Upcoming" value={s.upcoming || 0} />
      <Card label="Completed" value={s.completed || 0} />
      <Card label="Cancelled" value={s.cancelled || 0} />
      <Card label="No-shows" value={s.no_show || 0} />
      <Card label="Active encounters" value={d.encounters_active} />
      <Card label="Pending follow-ups" value={d.followups_pending} />
    </div>
  );
}

function Card({ label, value }) {
  return <div className="mg-card"><div className="text-[10px] uppercase font-bold text-slate-500">{label}</div><div className="text-2xl font-extrabold text-mg-navy mt-1">{value}</div></div>;
}

function DoctorActivityTab({ activity }) {
  return (
    <div className="mg-card" data-testid="doctor-activity">
      <ul className="space-y-2">
        {(activity || []).length === 0 && <li className="text-sm text-slate-400 text-center py-4">No activity yet.</li>}
        {(activity || []).map((a) => (
          <li key={a.log_id} className="border-b border-slate-50 pb-2 flex items-start gap-3 text-sm">
            <ShieldCheck className="w-4 h-4 text-mg-medical mt-0.5" />
            <div className="flex-1">
              <div><strong>{a.action}</strong> · {a.description || a.entity_type}</div>
              <div className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SPECIALIZATIONS = [
  "General Medicine", "Family Medicine", "Pediatrics", "Gynecology & Obstetrics",
  "Dermatology", "Orthopedics", "Cardiology", "Neurology", "Psychiatry", "ENT",
  "Ophthalmology", "Dentistry", "General Surgery", "Urology", "Gastroenterology",
  "Pulmonology", "Endocrinology", "Nephrology", "Oncology", "Radiology",
  "Anesthesiology", "Physiotherapy", "Dietetics & Nutrition", "Emergency Medicine",
  "Internal Medicine", "Rheumatology", "Hematology", "Pathology", "Plastic Surgery",
  "Vascular Surgery", "Neurosurgery", "Pediatric Surgery", "Diabetology",
  "Fertility / IVF", "Ayurveda", "Homeopathy", "Other"
];

// ============================================================
// EDIT / CREATE PAGE
// ============================================================
export function ClinicDoctorEditPage() {
  const links = useClinicLinks();
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) {
      setForm({
        salutation: "Dr.", name: "", email: "", phone: "", password: "",
        gender: "", specialization: "", sub_specialization: "",
        qualification: "", registration_number: "", registration_council: "",
        department: "", designation: "", years_of_experience: "",
        bio: "", languages: "",
        consultation_fee: 0, followup_fee: "", default_consultation_duration: 15,
        online_booking_enabled: true, public_directory_enabled: false, calendar_sync_enabled: false,
        internal_notes: "",
      });
    } else {
      api.get(`/clinic/doctors-x/${id}/detail`).then((r) => {
        const d = r.data;
        setForm({
          salutation: d.salutation || "Dr.", name: d.name || "", email: d.email || "", phone: d.phone || "",
          gender: d.gender || "", specialization: d.specialization || "",
          sub_specialization: d.sub_specialization || "",
          qualification: d.qualification || "",
          registration_number: d.registration_number || "",
          registration_council: d.registration_council || "",
          department: d.department || "", designation: d.designation || "",
          years_of_experience: d.years_of_experience || "",
          bio: d.bio || "", languages: (d.languages || []).join(", "),
          consultation_fee: d.consultation_fee || 0,
          followup_fee: d.followup_fee || "",
          default_consultation_duration: d.default_consultation_duration || 15,
          online_booking_enabled: d.online_booking_enabled !== false,
          public_directory_enabled: !!d.public_directory_enabled,
          calendar_sync_enabled: !!d.calendar_sync_enabled,
          internal_notes: d.internal_notes || "",
        });
      }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
    }
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (typeof payload.languages === "string") {
        payload.languages = payload.languages.split(",").map((s) => s.trim()).filter(Boolean);
      }
      // Convert blank strings to null so backend can clear optional fields
      const clearableStrings = [
        "phone", "gender", "specialization", "sub_specialization",
        "qualification", "registration_number", "registration_council",
        "department", "designation", "bio", "internal_notes",
      ];
      for (const k of clearableStrings) {
        if (payload[k] === "") payload[k] = null;
      }
      if (payload.years_of_experience === "" || payload.years_of_experience == null) payload.years_of_experience = null;
      if (payload.followup_fee === "" || payload.followup_fee == null) payload.followup_fee = null;
      payload.consultation_fee = Number(payload.consultation_fee) || 0;
      payload.default_consultation_duration = Number(payload.default_consultation_duration) || 15;
      if (isNew) {
        if (!payload.password) delete payload.password;
        const r = await api.post("/clinic/doctors-x", payload);
        toast.success(r.data?.temp_password ? `Doctor created. Temp password: ${r.data.temp_password}` : "Doctor created", { duration: 12000 });
        nav(`/clinic-admin/doctors/${r.data.doctor_id}`);
      } else {
        delete payload.password;
        await api.patch(`/clinic/doctors-x/${id}`, payload);
        toast.success("Saved");
        nav(`/clinic-admin/doctors/${id}`);
      }
    } catch (er) { toast.error(er?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  if (!form) return <DashboardLayout links={links} title="Doctor"><div className="mg-card text-center text-slate-400 py-10">Loading…</div></DashboardLayout>;
  return (
    <DashboardLayout links={links} title={isNew ? "New doctor" : "Edit doctor"}>
      <form onSubmit={submit} className="mg-card space-y-5 max-w-3xl">
        <div>
          <h3 className="font-bold text-mg-navy text-sm mb-3">Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Salutation">
              <select className="mg-input" value={form.salutation} onChange={(e) => set("salutation", e.target.value)}>
                <option value="Dr.">Dr.</option>
                <option value="Prof. Dr.">Prof. Dr.</option>
                <option value="Consultant Dr.">Consultant Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
              </select>
            </F>
            <F label="Name *"><input className="mg-input" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name" /></F>
            <F label="Email *"><input type="email" className="mg-input" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="field-email" /></F>
            <F label="Phone"><input className="mg-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="field-phone" /></F>
            <F label="Gender"><select className="mg-input" value={form.gender} onChange={(e) => set("gender", e.target.value)}><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select></F>
            {isNew && <F label="Temp password (auto if blank)"><input className="mg-input font-mono" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="field-password" /></F>}
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-bold text-mg-navy text-sm mb-3">Professional</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Specialization">
              <select className="mg-input" value={form.specialization} onChange={(e) => set("specialization", e.target.value)} data-testid="field-spec">
                <option value="">-- Select Specialization --</option>
                {SPECIALIZATIONS.map((spec) => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </select>
              {form.specialization === "Other" && (
                 <input className="mg-input mt-2" placeholder="Please specify" value={form.sub_specialization} onChange={(e) => set("sub_specialization", e.target.value)} />
              )}
            </F>
            <F label="Sub-specialization"><input className="mg-input" value={form.sub_specialization} onChange={(e) => set("sub_specialization", e.target.value)} /></F>
            <F label="Qualification"><input className="mg-input" value={form.qualification} onChange={(e) => set("qualification", e.target.value)} data-testid="field-qual" /></F>
            <F label="Registration #"><input className="mg-input" value={form.registration_number} onChange={(e) => set("registration_number", e.target.value)} data-testid="field-regno" /></F>
            <F label="Registration council"><input className="mg-input" value={form.registration_council} onChange={(e) => set("registration_council", e.target.value)} /></F>
            <F label="Department"><input className="mg-input" value={form.department} onChange={(e) => set("department", e.target.value)} /></F>
            <F label="Designation"><input className="mg-input" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></F>
            <F label="Years of experience"><input type="number" className="mg-input" value={form.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} /></F>
            <F label="Languages (comma-separated)" full><input className="mg-input" value={form.languages} onChange={(e) => set("languages", e.target.value)} /></F>
            <F label="Bio" full><textarea className="mg-input min-h-[80px]" value={form.bio} onChange={(e) => set("bio", e.target.value)} /></F>
          </div>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-bold text-mg-navy text-sm mb-3">Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Consultation fee (₹)"><input type="number" min="0" className="mg-input" value={form.consultation_fee} onChange={(e) => set("consultation_fee", e.target.value)} data-testid="field-fee" /></F>
            <F label="Follow-up fee (₹)"><input type="number" min="0" className="mg-input" value={form.followup_fee} onChange={(e) => set("followup_fee", e.target.value)} /></F>
            <F label="Default consultation duration (min)"><input type="number" min="5" className="mg-input" value={form.default_consultation_duration} onChange={(e) => set("default_consultation_duration", e.target.value)} data-testid="field-duration" /></F>
            <div className="md:col-span-2 space-y-2">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.online_booking_enabled} onChange={(e) => set("online_booking_enabled", e.target.checked)} data-testid="field-online" /> Online booking enabled</label>
              <label className="flex items-start gap-2 text-sm"><input className="mt-1" type="checkbox" checked={form.public_directory_enabled} onChange={(e) => set("public_directory_enabled", e.target.checked)} /><span>Show this doctor in Find Doctors<span className="block text-xs text-slate-500">When hidden, the doctor can still appear inside this clinic’s booking flow if online booking is enabled.</span></span></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.calendar_sync_enabled} onChange={(e) => set("calendar_sync_enabled", e.target.checked)} /> Calendar sync enabled</label>
            </div>
            <F label="Internal notes" full><textarea className="mg-input min-h-[60px]" value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} /></F>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Link to={isNew ? "/clinic-admin/doctors" : `/clinic-admin/doctors/${id}`} className="mg-btn-secondary text-sm">Cancel</Link>
          <button type="submit" disabled={busy} className="mg-btn-primary text-sm" data-testid="save-doctor"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </DashboardLayout>
  );
}

function F({ label, children, full }) {
  return <label className={`block ${full ? "md:col-span-2" : ""}`}><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;
}
